import { z } from 'zod'
import type { Mode } from './taxonomy'

/**
 * 메뉴 축 다축 꼬리표 (2026-10-22, 대표 명세 "AI_개발자_지시사항" 1장).
 * - 대분류·세부 나무가 "무엇을 만드는가" 라면 꼬리표는 "어떻게·무엇으로·누구에게·코스 어디" 다. 선택 칸이라 비워도 지금과 같다.
 * - 축 7개, 값 73개 (2026-10-23 에 스타일 4·할랄·도시락·케이터링 6개를 각 축 끝에 더함. 2026-11-01 대표 명세
 *   "카테고리 보강" 3장에서 단백질을 다섯 묶음 세부 값으로 나누고(+17), 5장에서 식이 제한에 알러지 주요 8종(+1),
 *   같은 날 전수 확인 뒤 대표 캡처의 기타 육류·한정 메뉴(+2)).
 *   화면 섹션은 명세의 다섯 묶음(MENU_TAG_SECTIONS), 식이 제한·서비스 형태는 세 축으로 나눴다.
 * - 서버는 프롬프트에 넣는다(menuTagsPromptLines). 식이 제한만 지켜야 하는 조건이고 나머지는 힌트다.
 *   대·중분류 골격과 categoryId 는 꼬리표 때문에 바뀌지 않는다.
 * - 값 id 는 한 번 정하면 바꾸지 않는다 (저장된 결과·글이 이 id 를 들고 있다). 라벨·hint 는 바꿔도 된다.
 * - 메뉴 축과 코스·테이스팅 축에 쓴다(isMenuLikeAxis). 식품 축 결과에는 꼬리표가 없다 (normalizeMenuTags 가 비운다).
 */
export const MENU_TAG_AXES = [
  'technique',
  'protein',
  'style',
  'diet',
  'service',
  'status',
  'course',
] as const
export type MenuTagAxis = (typeof MENU_TAG_AXES)[number]

/**
 * 1인분 접시로 내는 최상위 축: 메뉴, 그리고 코스·테이스팅 (2026-11-01 명세 0장: 역할을 고른 뒤 스타일·기법 선택).
 * 이 축만 메뉴 꼬리표를 받고, 가공식품 규칙(유통·표시·포장 사진·골든셋) 대신 메뉴 규칙을 쓴다.
 * 저장된 JSON 의 축 글자도 그대로 받는다. 식품 축(product)과 축이 없는 옛 행은 false.
 * taxonomy.ts 가 아니라 여기 두는 까닭: taxonomy.ts 가 코스 표를 만들 때 이 파일을 불러오므로 거꾸로 부르면 서로 기다린다
 */
export function isMenuLikeAxis(axis: string | null | undefined): boolean {
  return axis === 'menu' || axis === 'course'
}

/**
 * 단백질 축의 묶음 (화면 작은 제목). 2026-11-01 명세 3장의 다섯 묶음.
 * 묶음마다 처음에 하나로 넣었던 넓은 값(poultry, meat, plant-based, seasonal-local)은 지우지 않고 hiddenInPicker 로 남긴다.
 * 저장된 결과·글과 이미 스토어에 나간 앱이 그 id 를 보낸다. 해산물은 처음부터 6값이라 넓은 값이 없다
 */
export const MENU_TAG_GROUPS = ['seafood', 'poultry', 'meat', 'plant', 'seasonal'] as const
export type MenuTagGroup = (typeof MENU_TAG_GROUPS)[number]

export interface MenuTagValue {
  id: string
  ko: string
  en: string
  ja: string
  zh: string
  /** 생성 프롬프트 힌트 (한국어). 메뉴 세부 hint 와 같은 말투 */
  hint: string
  /** protein 의 묶음 (화면 작은 제목, 커뮤니티 거르기에서 넓은 값과 세부 값을 잇는다) */
  group?: MenuTagGroup
  /**
   * 새 앱 고르기 화면에서만 뺀다 (세부 값으로 나뉜 넓은 옛 값, 2026-11-01). 라벨·검증·정리·프롬프트는 그대로라
   * 저장된 결과·글과 옛 앱 요청이 그대로 동작한다. 이미 고른 값이면 고르기 화면에 보여서 끌 수 있다
   */
  hiddenInPicker?: boolean
}

const v = (
  id: string,
  ko: string,
  en: string,
  ja: string,
  zh: string,
  hint: string,
  group?: MenuTagGroup,
): MenuTagValue => (group ? { id, ko, en, ja, zh, hint, group } : { id, ko, en, ja, zh, hint })
/** 세부 값으로 나뉜 넓은 옛 값. id·라벨·hint 는 남기고 고르기 화면에서만 뺀다 (taxonomy 의 hidden 과 같은 방식) */
const broad = (tag: MenuTagValue): MenuTagValue => ({ ...tag, hiddenInPicker: true })

/**
 * 알러지 주요 8종 (식이 제한 major-allergens 의 hint). 흔히 '주요 8대 알레르기'로 묶는 목록(미국 FALCPA 의 8종)이다.
 * 판매 시장의 표시대상은 따로다 — 한국은 이보다 넓고(메밀·복숭아·토마토·고기류 등), 미국은 2023년부터 참깨를 더해 9종
 */
export const MAJOR_ALLERGENS_KO = '우유·달걀·생선·갑각류·견과류·땅콩·밀·대두'

export const MENU_TAGS: Record<MenuTagAxis, MenuTagValue[]> = {
  // ── 기법 중심 7 ──
  technique: [
    v(
      'sous-vide',
      '수비드·저온조리',
      'Sous-vide & low-temp',
      '真空低温調理',
      '低温慢煮',
      '진공 포장 후 정밀 온도 제어로 단백질의 수율(수분 손실)과 식감을 통제한다. 심부온도·시간표가 품질 축이고 선조리 후 급랭·보관해 주문 후에는 시어링 마감만 남는다. 진공포장기·수조 투자와 포장재가 원가, 저온 살균 기준(온도와 시간)을 steps 의 qc 에 적는다.',
    ),
    v(
      'confit',
      '컨핏',
      'Confit',
      'コンフィ',
      '油封',
      '재료를 오일이나 지방(오리기름·올리브유)에 잠기게 해 저온으로 오래 익히고 그 지방 속에 두어 숙성·보관한다. 예: 오리 컨핏, 마늘 컨핏, 토마토 컨핏. 지방 사용량과 재사용 횟수가 원가 축, 지방 속 보관으로 선조리 기간이 길다. 주문 후에는 껍질을 바삭하게 마감.',
    ),
    v(
      'fermentation',
      '발효·장 기반',
      'Fermentation & jang',
      '発酵・醤ベース',
      '发酵·酱基',
      '누룩, 장(된장·고추장·간장·젓갈), 김치 발효, 고지(코지·쌀누룩)를 맛의 뼈대로 쓴다. 발효 재료로 재우거나(시오코지·장 마리네이드) 직접 발효시킨다. 발효 기간·온도가 리드타임이고 배치 간 맛 편차 관리가 qc. 직접 담그는지 사서 쓰는지가 원가를 가른다.',
    ),
    v(
      'curing',
      '큐어링·염지',
      'Curing & brining',
      'キュアリング・塩漬け',
      '腌制·盐渍',
      '소금·설탕·향신료로 건식(문질러 재움) 또는 습식(염수) 큐어링해 수분을 빼고 맛을 들인다. 염지 시간과 염도가 축, 감량(수율)과 온도·시간 위생 관리가 원가와 qc. 예: 그라블락스, 염지 닭, 햄.',
    ),
    v(
      'smoking',
      '스모킹·훈연',
      'Smoking',
      'スモーク・燻製',
      '烟熏',
      '냉훈(약 30도 이하, 익히지 않고 향만)과 온훈(익히면서 향을 입힘)을 가르고 우드 칩 종류(참나무·사과나무·벚나무·히코리·메스키트)와 훈연 시간으로 향의 세기를 정한다. 메뉴 종류와 상관없이 훈연 향을 입히는 기법이라 훈제 연어 전채, 훈연 버터, 스모크 칵테일에도 쓴다. 저온 장시간 바비큐가 메뉴 자체면 세부 훈연·바비큐의 전일 준비를 따른다. 훈연 장비·칩·시간이 축, 감량과 전일 준비 여부. 냉훈이면 별도 가열 공정과 위생 관리.',
    ),
    v(
      'aging',
      '숙성·에이징',
      'Aging',
      '熟成・エイジング',
      '熟成',
      '건식(드라이)·습식(웻) 숙성으로 육류·해산물·치즈의 풍미를 올린다. 숙성 기간(일 단위)과 감량·트리밍 손실이 원가 축이고 온도·습도를 맞춘 숙성고가 진입 조건. 생선 숙성은 손질·탈수 공정과 위생 기준을 qc 에 적는다. 객단가를 높게 잡는다.',
    ),
    v(
      'modernist',
      '분자·현대 기법',
      'Modernist techniques',
      '分子・モダン技法',
      '分子·现代技法',
      '젤화, 에멀전, 스페리피케이션(구체화), 액화질소, 폼·에스푸마 같은 현대 기법. 첨가 소재(한천·젤란·알긴산·레시틴)와 전용 도구(사이펀·액화질소 용기)가 원가, 재현성과 제공 직전 작업 시간이 축. 액화질소를 쓰면 취급 안전을 caution 에 적는다. 코스·시그니처용 소량 구성.',
    ),
  ],
  // ── 단백질·재료 중심 27 (2026-11-01 명세 3장: 다섯 묶음, 고르기 화면 23 + 숨긴 넓은 옛 값 4) ──
  // 묶음 순서가 화면 순서다. 넓은 옛 값(broad)은 묶음 맨 앞, 그 뒤에 명세의 세부 값
  protein: [
    // 해산물 6
    v(
      'seafood-white',
      '흰살생선',
      'White fish',
      '白身魚',
      '白肉鱼',
      '흰살 생선(광어·도미·대구·농어). 선도·손질 수율과 시가가 원가 축, 살이 부서지기 쉬워 가열을 짧게.',
      'seafood',
    ),
    v(
      'seafood-oily',
      '등푸른생선',
      'Oily fish',
      '青魚',
      '青背鱼',
      '등푸른 생선(고등어·연어·참치·방어). 지방이 많아 산패·비린내 관리가 qc, 큐어링·훈연·구이와 잘 맞는다. 시가 변동.',
      'seafood',
    ),
    v(
      'seafood-shellfish',
      '조개류',
      'Shellfish',
      '貝類',
      '贝类',
      '조개류(바지락·홍합·가리비·굴). 해감·생존 관리와 껍데기 뺀 수율이 원가 축, 충분한 가열 등 위생 주의.',
      'seafood',
    ),
    v(
      'seafood-crustacean',
      '갑각류',
      'Crustaceans',
      '甲殻類',
      '甲壳类',
      '갑각류(새우·랍스터·게). 껍질 손질 인력과 수율이 원가 축, 갑각류 알레르기 표시 필수, 원가 높고 시가.',
      'seafood',
    ),
    v(
      'seafood-cephalopod',
      '두족류',
      'Octopus & squid',
      '頭足類',
      '头足类',
      '두족류(문어·오징어·주꾸미·낙지). 데치기·연육 공정과 수축 감량이 원가 축, 질겨지지 않는 가열 시간이 qc.',
      'seafood',
    ),
    v(
      'seafood-seaweed',
      '해조류',
      'Seaweed',
      '海藻',
      '海藻',
      '해조류(미역·다시마·김·톳·매생이). 불림·염장 해제 수율이 원가 축, 감칠맛·식감 재료로 쓰고 계절 편중.',
      'seafood',
    ),
    // 가금 4
    broad(
      v(
        'poultry',
        '가금',
        'Poultry',
        '家禽',
        '禽肉',
        '가금(닭·오리·메추리). 부위별 원가 차가 크고 심부온도 기준이 qc, 껍질 마감이 품질 축.',
        'poultry',
      ),
    ),
    v(
      'poultry-chicken',
      '닭',
      'Chicken',
      '鶏肉',
      '鸡肉',
      '닭(통닭·다리·가슴·날개). 부위별 원가와 수율 차가 크고 심부온도 기준이 qc, 염지 여부와 껍질 마감이 품질 축.',
      'poultry',
    ),
    v(
      'poultry-duck',
      '오리',
      'Duck',
      '鴨肉',
      '鸭肉',
      '오리(가슴·다리·통오리). 두꺼운 지방층을 녹여 내는 렌더링과 껍질 마감이 축, 나온 기름은 다른 조리에 다시 쓴다. 가슴살 굽기 정도와 심부온도 기준을 qc 에 적는다. 단가는 닭보다 높다.',
      'poultry',
    ),
    v(
      'poultry-quail',
      '메추리',
      'Quail',
      'うずら',
      '鹌鹑',
      '메추리(통메추리·메추리알). 1마리가 작아 1인 1~2마리 단위, 뼈 바르기 같은 손질 인건비가 원가 축이고 가열 시간이 짧다.',
      'poultry',
    ),
    v(
      'poultry-other',
      '기타 가금',
      'Other poultry',
      'その他の家禽',
      '其他禽类',
      '닭·오리·메추리 밖의 가금(꿩·거위·뿔닭·비둘기 등). 수급처가 좁아 발주 리드타임과 대체 재료가 질문이고 시가로 본다.',
      'poultry',
    ),
    // 육류 6 (명세: 소·돼지·양·사슴·토끼. 대표 캡처의 '기타 (사슴·토끼 등)' 자리로 기타 육류를 끝에, 2026-11-01 전수 확인 21번)
    broad(
      v(
        'meat',
        '육류',
        'Meat',
        '肉類',
        '畜肉',
        '육류(소·돼지·양). 부위·등급과 원육 시세가 원가를 지배하고 손질 감량이 수율. 굽기 정도 선택과 원산지 표시가 질문.',
        'meat',
      ),
    ),
    v(
      'meat-beef',
      '소고기',
      'Beef',
      '牛肉',
      '牛肉',
      '소고기(부위·등급·원산지). 원육 시세가 원가를 지배하고 손질·트리밍 감량이 수율. 굽기 정도 선택과 원산지 표시가 질문.',
      'meat',
    ),
    v(
      'meat-pork',
      '돼지고기',
      'Pork',
      '豚肉',
      '猪肉',
      '돼지고기(삼겹·목살·앞다리·등심). 소보다 단가가 낮고 부위 수급이 안정적, 심부온도 기준이 qc. 원산지 표시.',
      'meat',
    ),
    v(
      'meat-lamb',
      '양고기',
      'Lamb',
      'ラム・羊肉',
      '羊肉',
      '양고기(램·머튼, 랙·숄더·다리). 특유의 향을 향신료·허브로 다루는 것이 축, 수입 냉장·냉동 수급과 단가가 질문.',
      'meat',
    ),
    v(
      'meat-venison',
      '사슴고기',
      'Venison',
      '鹿肉',
      '鹿肉',
      '사슴고기. 지방이 거의 없어 과조리하면 퍽퍽해지므로 짧은 가열과 휴지가 축. 수입·농장 수급이 좁아 시가로 보고 대체 재료를 적는다.',
      'meat',
    ),
    v(
      'meat-rabbit',
      '토끼고기',
      'Rabbit',
      'うさぎ肉',
      '兔肉',
      '토끼고기. 살이 담백하고 작아 부위별 손질(다리는 컨핏·브레이징, 등심은 구이) 인건비가 원가 축. 수급처가 좁아 발주 리드타임과 대체 재료를 적는다.',
      'meat',
    ),
    v(
      'meat-other',
      '기타 육류',
      'Other meat',
      'その他の肉',
      '其他肉类',
      '소·돼지·양·사슴·토끼 밖의 육류(염소·말고기·멧돼지·들소). 특유의 향과 질긴 부위를 다루는 손질·숙성이 축. 수급처가 좁아 발주 리드타임과 대체 재료가 질문이고 시가로 본다. 원산지 표시.',
      'meat',
    ),
    // 채식·플랜트 베이스 4
    broad(
      v(
        'plant-based',
        '채식·플랜트베이스',
        'Plant-based',
        '菜食・プラントベース',
        '素食·植物基',
        '두부·콩·버섯·곡물·채소가 주재료. 단백질을 무엇으로 채우는가와 감칠맛 보강(버섯·발효)이 축, 재료 원가는 낮고 손이 많다.',
        'plant',
      ),
    ),
    v(
      'plant-tofu',
      '두부·콩',
      'Tofu & beans',
      '豆腐・豆',
      '豆腐·豆类',
      '두부·콩(두부·유부·템페·콩류)이 단백질 중심. 물기 빼기·굽기로 식감을 만들고 간을 깊게 들이는 것이 축, 대두 알레르기 표시.',
      'plant',
    ),
    v(
      'plant-mushroom',
      '버섯',
      'Mushrooms',
      'きのこ',
      '菌菇',
      '버섯(표고·새송이·느타리·양송이 등)이 중심. 감칠맛과 고기 같은 식감이 축, 수분이 많아 굽기 감량이 크고 품종별 시세 차이가 있다.',
      'plant',
    ),
    v(
      'plant-vegetable',
      '채소 중심',
      'Vegetable-forward',
      '野菜中心',
      '以蔬菜为主',
      '채소가 주인공. 손질 인건비와 감량이 원가 축, 굽기·절임·퓌레 같은 조리법으로 맛의 층을 만든다. 단백질을 무엇으로 보강할지 적는다.',
      'plant',
    ),
    v(
      'plant-grain',
      '그레인 중심',
      'Grain-forward',
      '穀物中心',
      '以谷物为主',
      '곡물(현미·보리·귀리·퀴노아·파로 등)이 중심. 불림·삶기 선조리와 보관이 공정 축, 원가는 낮고 포만감 위주. 밀·보리는 글루텐이 있다.',
      'plant',
    ),
    // 시즌·로컬 4
    broad(
      v(
        'seasonal-local',
        '시즌·로컬',
        'Seasonal & local',
        '旬・ローカル',
        '时令·本地',
        '제철·지역 식재료가 주인공. 공급 기간이 짧아 메뉴 수명과 대체 재료 계획이 질문, 산지 직거래 단가와 수급 변동이 원가 축.',
        'seasonal',
      ),
    ),
    v(
      'seasonal-vegetable',
      '제철 채소',
      'Seasonal vegetables',
      '旬の野菜',
      '时令蔬菜',
      '제철 채소가 주인공. 공급 기간이 짧아 메뉴 수명과 시즌이 끝난 뒤 바꿀 재료가 질문, 산지 단가 변동이 원가 축.',
      'seasonal',
    ),
    v(
      'seasonal-seafood',
      '제철 해산물',
      'Seasonal seafood',
      '旬の魚介',
      '时令海鲜',
      '철이 정해진 해산물(대게·전어·굴·방어 등)이 주인공. 시가와 산지 수급이 원가 축, 선도 관리가 qc. 시즌이 끝나면 바꿀 재료를 적는다.',
      'seasonal',
    ),
    v(
      'local-produce',
      '로컬 농산물',
      'Local produce',
      '地元の農産物',
      '本地农产品',
      '가까운 산지의 농산물을 직거래로 쓴다. 산지 표기가 메뉴의 가치이고, 소량 직거래 단가와 배송 주기, 수급이 끊길 때의 대체 계획이 질문.',
      'seasonal',
    ),
    v(
      'limited-ingredient',
      '한정 재료',
      'Limited ingredient',
      '限定食材',
      '限定食材',
      '수량이 정해진 재료(희귀 품종·소량 생산품)가 주인공. 하루 판매 수량 상한과 재료가 떨어지면 메뉴를 내린다는 운영 메모를 적고 단가는 높게 본다.',
      'seasonal',
    ),
  ],
  // ── 요리 스타일 10 ──
  style: [
    v(
      'korean-modern',
      '한식 모던',
      'Modern Korean',
      'モダン韓国料理',
      '现代韩餐',
      '전통 한식 기법(장·발효·나물·숯불)으로 맛을 내고 플레이팅·코스 구성은 현대식으로 다시 짠다. 전통 재료의 현대적 손질과 1인 플레이팅이 축.',
    ),
    v(
      'french',
      '프렌치',
      'French',
      'フレンチ',
      '法式',
      '클래식과 모던 프렌치 모두. 소스(퐁·리덕션·버터 소스)와 가니시 구성, 정교한 플레이팅이 축. 소스 선조리 노동과 버터·크림 원가가 크다.',
    ),
    v(
      'italian',
      '이탈리안',
      'Italian',
      'イタリアン',
      '意式',
      '지역 요리(토스카나·시칠리아·에밀리아 등) 포함. 재료 수가 적고 원재료 품질(올리브유·치즈·토마토)이 맛을 정한다. 파스타·리조또·구이의 단순한 공정.',
    ),
    v(
      'japanese',
      '재패니즈',
      'Japanese',
      '和食',
      '日式',
      '카이세키(제철 재료·정해진 코스 순서)와 모던 재패니즈 모두. 다시·간장·미림 기반의 섬세한 간과 칼 손질, 계절감 있는 소량 구성과 그릇 연출.',
    ),
    v(
      'spanish-tapas',
      '스페니시·타파스',
      'Spanish & tapas',
      'スパニッシュ・タパス',
      '西班牙·塔帕斯',
      '스몰 플레이트 중심. 작은 접시 여러 개를 나눠 먹는 구성이라 1접시 소량 단가와 회전이 축, 올리브유·파프리카·하몽 같은 재료.',
    ),
    v(
      'fusion',
      '퓨전·크리에이티브',
      'Fusion & creative',
      'フュージョン・クリエイティブ',
      '融合·创意',
      '특정 스타일에 묶이지 않고 두 요리 문화 이상을 섞는다. 익숙한 맛 하나를 기준으로 낯선 요소는 한두 개만 넣어야 메뉴가 읽힌다.',
    ),
    // 2026-10-23 추가 4 (한식은 모던 하나뿐이었고 중식·동남아·아메리칸 자리가 없었다)
    v(
      'korean-classic',
      '한식 전통·대중',
      'Korean classic',
      '韓国料理（伝統・大衆）',
      '传统韩餐',
      '국밥·백반·고깃집처럼 익숙한 한식 맛을 지킨다. 재해석보다 재현과 회전이 축이고, 반찬 가짓수와 리필이 원가를 흔든다.',
    ),
    v(
      'chinese',
      '중식',
      'Chinese',
      '中華',
      '中餐',
      '웍 화력과 전분 소스 농도가 축. 짜장·짬뽕·탕수육·딤섬처럼 기름·전분·센 불 조리가 많고 배달 포장 뒤 식감이 질문.',
    ),
    v(
      'asian',
      '동남아·인도',
      'Southeast Asian & Indian',
      '東南アジア・インド',
      '东南亚·印度菜',
      '피시소스·코코넛·향신료 배합이 축. 수입 소스 수급과 매운맛 조절, 향신료를 직접 볶는지 페이스트를 쓰는지가 질문.',
    ),
    v(
      'american-mexican',
      '아메리칸·멕시칸',
      'American & Mexican',
      'アメリカン・メキシカン',
      '美式·墨西哥菜',
      '버거·바비큐·타코처럼 큰 양과 소스·치즈가 축. 토르티야·번 수급과 훈연·그릴 설비가 질문.',
    ),
  ],
  // ── 식이 제한 7 (생성 프롬프트에서 힌트가 아니라 지켜야 하는 조건, menuDietLines) ──
  diet: [
    v(
      'vegan',
      '비건',
      'Vegan',
      'ヴィーガン',
      '纯素',
      '고기·생선·해산물·달걀·유제품·꿀·젤라틴·동물성 육수를 쓰지 않는다. 대체 재료(두부·식물성 크림·채수)와 교차오염 관리 메모.',
    ),
    v(
      'vegetarian',
      '베지테리안',
      'Vegetarian',
      'ベジタリアン',
      '蛋奶素',
      '고기·생선·해산물·동물성 육수를 쓰지 않는다. 달걀·유제품은 써도 된다.',
    ),
    v(
      'gluten-free',
      '글루텐프리',
      'Gluten-free',
      'グルテンフリー',
      '无麸质',
      '밀·보리·호밀과 그 가공품(일반 간장, 밀가루 튀김옷 포함)을 쓰지 않고 쌀가루·전분·글루텐프리 간장으로 바꾼다. 대체 재료 원가 상승과 교차오염 메모.',
    ),
    v(
      'dairy-free',
      '유제품 프리',
      'Dairy-free',
      '乳製品不使用',
      '无乳制品',
      '우유·버터·크림·치즈·유청을 쓰지 않고 오트·코코넛 같은 식물성 대체로 바꾼다.',
    ),
    v(
      'nut-free',
      '견과류 프리',
      'Nut-free',
      'ナッツ不使用',
      '无坚果',
      '땅콩·호두·아몬드·잣 등 견과와 그 오일·페이스트를 쓰지 않는다. 알레르기 표시와 교차오염 메모.',
    ),
    // 2026-11-01 명세 5장. 명세 순서대로 견과류 프리 뒤 (할랄은 10/23 에 더한 값이라 맨 끝 그대로).
    // '고려' = 주요 8종을 빼고 짜되, 사용자가 단백질·재료로 직접 고른 주재료는 남긴다 (비건처럼 단백질을 지우지 않는다)
    v(
      'major-allergens',
      '알러지 주요 8종 고려',
      'Top 8 allergens aware',
      '主要アレルゲン8品目に配慮',
      '考虑8大过敏原',
      `알레르기 주요 8종(${MAJOR_ALLERGENS_KO})을 재료·양념·육수·소스·튀김옷·대체재에 쓰지 않는다. 간장(밀·대두), 버터·크림(우유), 마요네즈(달걀), 피시소스(생선)처럼 숨은 원료도 뺀다. 단백질·재료로 직접 고른 것이 8종에 들면(예: 흰살생선, 갑각류, 두부·콩) 그 주재료만 남긴다. 남는 8종은 판매 시장 표시대상이면 allergens 에, 아니면 caution 에 적는다.`,
    ),
    // 2026-10-23 추가. 결과 화면에서 잠기는 것(반박 검토 H1)과 표시 단정 금지(H2)는 다른 식이 제한과 같다
    v(
      'halal',
      '할랄',
      'Halal',
      'ハラール',
      '清真',
      '돼지와 돼지 유래 원료(라드·젤라틴), 알코올(맛술·주정·술 소스)을 쓰지 않는다. 육류·가금은 할랄 원료로 적는다. 인증 기관 확인과 조리 도구 분리를 caution 에 적는다.',
    ),
  ],
  // ── 서비스 형태 6 ──
  service: [
    v(
      'brunch',
      '브런치',
      'Brunch',
      'ブランチ',
      '早午餐',
      '오전에서 이른 오후 판매. 계란·빵·샐러드 조합과 음료 세트가 객단가를 만든다. 주말 피크 회전.',
    ),
    v(
      'lunch-set',
      '런치 세트',
      'Lunch set',
      'ランチセット',
      '午市套餐',
      '정해진 시간에 몰리는 주문. 선조리 비율과 제공 시간(10분 안팎)이 축, 객단가 상한이 낮아 원가율 관리가 핵심.',
    ),
    v(
      'dinner',
      '디너',
      'Dinner',
      'ディナー',
      '晚餐',
      '체류가 길고 주류 페어링·코스로 객단가를 올린다. 주문 후 조리 비중이 높아도 된다.',
    ),
    v(
      'bar-snack',
      '바 스낵',
      'Bar snack',
      'バースナック',
      '酒吧小食',
      '술과 함께 집어 먹는 소량 메뉴. 출수 속도, 1접시 단가, 술 페어링이 축.',
    ),
    v(
      'tasting-course',
      '테이스팅 코스용',
      'For tasting course',
      'テイスティングコース用',
      '品鉴套餐用',
      '코스 안의 한 접시. 1인분이 한 입에서 소량이고 구성 요소 수가 인건비 축. 코스 전체 가격 중 이 접시의 원가 몫을 함께 적는다.',
    ),
    // 2026-10-23 추가
    v(
      'catering',
      '도시락·케이터링',
      'Lunch boxes & catering',
      '弁当・ケータリング',
      '便当·外烩',
      '조리 뒤 옮겨서 먹는다. 식은 상태의 품질과 온도·시간 관리, 1식 단가 상한이 축. 단체급식 포함.',
    ),
  ],
  // ── 메뉴 상태 5 (명세 4 + 대표 캡처의 '한정 메뉴', 2026-11-01 전수 확인 21번) ──
  status: [
    v(
      'signature',
      '시그니처',
      'Signature',
      'シグネチャー',
      '招牌',
      '매장 대표 메뉴. 맛 재현성(표준 레시피)과 원가 고정이 우선, 객단가는 상단.',
    ),
    v(
      'seasonal-limited',
      '시즌 한정',
      'Seasonal limited',
      '季節限定',
      '季节限定',
      '판매 기간이 정해져 있다. 재료 발주량·재고 소진 계획과 시즌이 끝난 뒤 남는 재료 활용을 baseNote 에 적는다.',
    ),
    v(
      'testing',
      '테스트 중',
      'Testing',
      'テスト中',
      '测试中',
      '확정 전 메뉴. 가정한 값은 중요한 것만 internal.assumptions 에 20개 안으로 적고, 원가는 범위로 보고 검증할 항목을 남긴다.',
    ),
    v(
      'regular',
      '상시 메뉴',
      'Regular menu',
      '定番メニュー',
      '常规菜单',
      '연중 판매. 수급이 안정된 재료와 표준화된 공정, 대량 선조리 효율이 우선.',
    ),
    // 계절과 상관없이 수량·기간을 정해 파는 메뉴. 시즌 한정과 나눴다 (상태는 하나만 고른다)
    v(
      'limited',
      '한정 메뉴',
      'Limited menu',
      '限定メニュー',
      '限定菜单',
      '계절과 상관없이 수량이나 기간을 정해 판다(하루 몇 그릇 한정, 협업·행사 메뉴). 한정 수량에 맞춘 재료 발주와 판매가 끝난 뒤 남는 재료 활용을 baseNote 에 적고, 적은 양을 만들 때의 원가 상승을 본다. 계절 재료로 기간이 정해지면 시즌 한정.',
    ),
  ],
  // ── 코스·테이스팅 하위 역할 10 ──
  // 이름이 코스·테이스팅 칸의 역할 이름이다 (course-taxonomy.ts 가 4개 언어 모두 여기서 읽는다). 메뉴 꼬리표 화면과 코스 칸이
  // 같은 이름을 보이게 2026-11-01 점검 5번에서 ko 를 명세 6장 표기로 맞췄다 (그전 '아뮤즈·한입'·'찬 전채'처럼 한글 표기).
  // 시드 course_role 은 en 표기로 들어온다
  course: [
    v(
      'amuse',
      'Amuse·한 입',
      'Amuse-bouche',
      'アミューズ',
      '开胃小点',
      '코스 첫 한 입(10~30g). 손님이 고르지 않는 셰프 제공 접시라 원가는 코스 가격에 묻힌다. 제공 직전 조립 속도가 축.',
    ),
    v(
      'starter-cold',
      'Starter·Cold',
      'Cold starter',
      '冷前菜',
      '冷前菜',
      '찬 전채. 선조리·냉장 보관 후 플레이팅만, 산미·식감 대비. 1인 40~80g.',
    ),
    v(
      'starter-hot',
      'Starter·Hot',
      'Hot starter',
      '温前菜',
      '热前菜',
      '따뜻한 전채. 주문 후 짧은 가열(시어링·튀김·수프 마감). 1인 50~100g.',
    ),
    v(
      'intermediate',
      'Intermediate',
      'Intermediate',
      '中間皿',
      '中间菜',
      '전채와 메인 사이 접시(작은 파스타·리조또·수프). 탄수화물·감칠맛으로 흐름을 잇는 소량.',
    ),
    v(
      'fish',
      'Fish course',
      'Fish course',
      '魚料理',
      '鱼料理',
      '생선·해산물 접시. 가열 시간이 짧고 마감 직전 조리, 소스는 가볍다. 1인 60~100g.',
    ),
    v(
      'main',
      'Main',
      'Main',
      'メイン',
      '主菜',
      '코스의 중심 접시. 단백질 1인 120~180g과 가니시 2~3가지, 코스 원가에서 가장 큰 몫.',
    ),
    v(
      'pre-dessert',
      'Pre-dessert',
      'Pre-dessert',
      'プレデセール',
      '前甜点',
      '메인 뒤 입가심(셔벗·그라니타·젤리). 산미·청량감, 선제조 냉동 보관.',
    ),
    v(
      'dessert',
      'Dessert',
      'Dessert',
      'デセール',
      '甜点',
      '코스 디저트 접시. 주문 후 플레이팅, 온도 대비와 구성 요소 수가 인건비.',
    ),
    v(
      'mignardise',
      'Mignardise',
      'Mignardise',
      'ミニャルディーズ',
      '小甜点',
      '커피·차와 내는 한입 과자(봉봉·마카롱·파트 드 프뤼). 대량 선제조 후 개수 단위 제공.',
    ),
    v(
      'cheese',
      'Cheese course',
      'Cheese course',
      'チーズコース',
      '奶酪拼盘',
      '치즈 2~4종과 곁들임(과일·견과·꿀·빵). 조리보다 숙성 치즈 매입·보관(온도·습도)과 1인 중량(40~60g)이 원가 축.',
    ),
  ],
}

/** 축마다 고를 수 있는 최대 개수 (status·course 는 하나만) */
export const MENU_TAG_LIMITS: Record<MenuTagAxis, number> = {
  technique: 3,
  protein: 4,
  style: 2,
  diet: 5,
  service: 2,
  status: 1,
  course: 1,
}

/** 축 라벨 (칩·프롬프트·diet-service 섹션의 작은 제목) */
const AXIS_LABELS: Record<MenuTagAxis, { ko: string; en: string; ja: string; zh: string }> = {
  technique: { ko: '기법', en: 'Technique', ja: '技法', zh: '技法' },
  protein: { ko: '단백질·재료', en: 'Protein', ja: 'たんぱく質・食材', zh: '蛋白质·食材' },
  style: { ko: '요리 스타일', en: 'Style', ja: 'スタイル', zh: '风格' },
  diet: { ko: '식이 제한', en: 'Diet', ja: '食事制限', zh: '饮食限制' },
  service: { ko: '서비스 형태', en: 'Service', ja: '提供形態', zh: '供应形式' },
  status: { ko: '메뉴 상태', en: 'Menu status', ja: 'メニュー状態', zh: '菜单状态' },
  course: { ko: '코스 역할', en: 'Course role', ja: 'コースの役割', zh: '套餐角色' },
}

/** protein 묶음의 작은 제목 (명세 3장 표기) */
export const MENU_TAG_GROUP_LABELS: Record<
  MenuTagGroup,
  { ko: string; en: string; ja: string; zh: string }
> = {
  seafood: { ko: '해산물', en: 'Seafood', ja: '魚介', zh: '海鲜' },
  poultry: { ko: '가금', en: 'Poultry', ja: '家禽', zh: '禽类' },
  meat: { ko: '육류', en: 'Meat', ja: '肉類', zh: '畜肉' },
  plant: {
    ko: '채식·플랜트 베이스',
    en: 'Plant-based',
    ja: '菜食・プラントベース',
    zh: '素食·植物基',
  },
  seasonal: { ko: '시즌·로컬', en: 'Seasonal & local', ja: '旬・ローカル', zh: '时令·本地' },
}

export type MenuTagSectionId = 'technique' | 'protein' | 'style' | 'diet-service' | 'course'
export interface MenuTagSection {
  id: MenuTagSectionId
  axes: MenuTagAxis[]
  /**
   * true 면 사진을 쓰는 섹션, false 면 글자 칩만. 사진을 쓰는 모양은 피커가 축마다 정한다:
   * 값마다 사진 타일(기법·코스 역할), 설명이 있는 축은 사진 + 이름 + 설명 행(요리 스타일, menu-tag-notes),
   * 묶음이 있는 축은 묶음 머리에 사진 + 글자 칩(단백질, 값이 23개라 타일로는 너무 길다)
   */
  tiles: boolean
  ko: string
  en: string
  ja: string
  zh: string
  /** 이 섹션에서 그리지 않는 값 (축 → 값 id). 최상위 축이 이미 말해 주는 값을 다시 묻지 않을 때만 (menuTagSectionsFor) */
  omit?: Partial<Record<MenuTagAxis, readonly string[]>>
}

/** 화면 섹션 (명세의 다섯 묶음). diet-service 의 작은 제목은 menuTagAxisLabel 로 */
export const MENU_TAG_SECTIONS: MenuTagSection[] = [
  {
    id: 'technique',
    axes: ['technique'],
    tiles: true,
    ko: '기법 중심',
    en: 'Technique',
    ja: '技法',
    zh: '技法',
  },
  {
    id: 'protein',
    axes: ['protein'],
    tiles: true,
    ko: '단백질·재료 중심',
    en: 'Protein & ingredients',
    ja: 'たんぱく質・食材',
    zh: '蛋白质·食材',
  },
  {
    id: 'style',
    axes: ['style'],
    tiles: true,
    ko: '요리 스타일',
    en: 'Cuisine style',
    ja: '料理スタイル',
    zh: '菜系风格',
  },
  {
    id: 'diet-service',
    axes: ['diet', 'service', 'status'],
    tiles: false,
    ko: '식이 제한·서비스 형태',
    en: 'Diet & service',
    ja: '食事制限・提供形態',
    zh: '饮食限制·供应形式',
  },
  {
    id: 'course',
    axes: ['course'],
    tiles: true,
    ko: '코스·테이스팅 하위 역할',
    en: 'Course role',
    ja: 'コースの役割',
    zh: '套餐角色',
  },
]

/**
 * 서비스 형태 '테이스팅 코스용'. 코스·테이스팅 축은 이미 코스 접시라 이 값을 묻지 않고(menuTagSectionsFor),
 * 정리(normalizeMenuTags)에서도 뺀다 (2026-11-01 점검 10번). 메뉴 축에서는 그대로 고른다
 */
const TASTING_COURSE_SERVICE = 'tasting-course'

/**
 * 최상위 축에 맞는 화면 섹션. 메뉴 축은 다섯 섹션 그대로. 코스·테이스팅 축은 축이 이미 말해 주는 것을 다시 묻지 않는다:
 * 코스 역할 섹션(역할을 세부로 이미 골랐다, 2026-11-01 명세 0장)과 서비스 형태의 '테이스팅 코스용'
 */
export function menuTagSectionsFor(axis: string | null | undefined): MenuTagSection[] {
  if (axis !== 'course') return MENU_TAG_SECTIONS
  return MENU_TAG_SECTIONS.filter((s) => s.id !== 'course').map((s) =>
    s.axes.includes('service') ? { ...s, omit: { service: [TASTING_COURSE_SERVICE] } } : s,
  )
}

/** 축별 값 id 목록. 비어 있는 축은 키가 없다 */
export type MenuTags = Partial<Record<MenuTagAxis, string[]>>

const tagIds = z.array(z.string().min(1).max(40)).max(10).optional()

/**
 * 입력 검증. 개수 상한은 느슨하게(10), 실제 상한·충돌 정리는 normalizeMenuTags 가 한다.
 * 모르는 축 키는 zod object 가 버린다 (옛 서버도 같은 이유로 새 앱의 칸을 오류 없이 버린다)
 */
export const menuTagsSchema = z.object({
  technique: tagIds,
  protein: tagIds,
  style: tagIds,
  diet: tagIds,
  service: tagIds,
  status: tagIds,
  course: tagIds,
})

export function findMenuTag(axis: MenuTagAxis, id: string): MenuTagValue | undefined {
  return MENU_TAGS[axis]?.find((x) => x.id === id)
}

function pick(
  labels: { ko: string; en: string; ja: string; zh: string },
  language: string,
): string {
  if (language === 'ko' || language === 'ja' || language === 'zh') return labels[language]
  return labels.en
}

/** 값 라벨. 모르는 id 는 id 그대로 돌려준다 */
export function menuTagLabel(axis: MenuTagAxis, id: string, language: string): string {
  const tag = findMenuTag(axis, id)
  return tag ? pick(tag, language) : id
}

export function menuTagAxisLabel(axis: MenuTagAxis, language: string): string {
  const labels = AXIS_LABELS[axis]
  return labels ? pick(labels, language) : axis
}

/**
 * vegan·vegetarian 이 있으면 지우는 protein 값 (제약이 주재료보다 이긴다).
 * 해조류·채식 묶음·제철 채소·로컬 농산물·한정 재료는 남긴다. 제철 해산물은 동물성이라 지운다
 */
const ANIMAL_PROTEINS = new Set([
  'meat',
  'meat-beef',
  'meat-pork',
  'meat-lamb',
  'meat-venison',
  'meat-rabbit',
  'meat-other',
  'poultry',
  'poultry-chicken',
  'poultry-duck',
  'poultry-quail',
  'poultry-other',
  'seafood-white',
  'seafood-oily',
  'seafood-shellfish',
  'seafood-crustacean',
  'seafood-cephalopod',
  'seasonal-seafood',
])

/**
 * 꼬리표 정리. 서버(생성·수정·글)와 앱(피커)이 같은 함수를 쓴다.
 * 1. 꼬리표를 받지 않는 축(식품, 축 없는 옛 행)이면 {} 2. 모르는 축·값은 버리고 중복은 하나만 3. vegan 이 있으면 vegetarian 을 지운다
 * 4. vegan·vegetarian 이 있으면 동물성 protein 을 지운다 5. 축 상한을 넘으면 앞에서부터 그 개수만
 * 6. 빈 축은 키를 지우고 MENU_TAGS 값 순서로 정렬 (같은 선택이면 같은 결과)
 * 7. 코스·테이스팅 축이면 코스 역할 꼬리표를 뺀다 (역할이 대분류다. 둘이 다르면 프롬프트가 두 역할을 받는다).
 *    서비스 형태 '테이스팅 코스용' 도 뺀다 (축이 이미 코스 접시라고 말한다)
 * 저장된 JSON 을 그대로 받을 수 있게 모양이 틀린 입력(배열 아님, 문자열 아님)도 오류 없이 버린다
 */
export function normalizeMenuTags(
  tags: MenuTags | null | undefined,
  axis: string | null | undefined,
): MenuTags {
  if (!isMenuLikeAxis(axis) || !tags || typeof tags !== 'object' || Array.isArray(tags)) return {}
  const picked = {} as Record<MenuTagAxis, string[]>
  for (const a of MENU_TAG_AXES) {
    const raw: unknown = (tags as Record<string, unknown>)[a]
    const ids = Array.isArray(raw) ? raw : []
    const seen = new Set<string>()
    picked[a] = ids.filter(
      (id): id is string =>
        typeof id === 'string' && !seen.has(id) && !!findMenuTag(a, id) && (seen.add(id), true),
    )
  }
  if (picked.diet.includes('vegan')) picked.diet = picked.diet.filter((id) => id !== 'vegetarian')
  if (picked.diet.includes('vegan') || picked.diet.includes('vegetarian'))
    picked.protein = picked.protein.filter((id) => !ANIMAL_PROTEINS.has(id))
  if (axis === 'course') {
    picked.course = []
    picked.service = picked.service.filter((id) => id !== TASTING_COURSE_SERVICE)
  }
  const out: MenuTags = {}
  for (const a of MENU_TAG_AXES) {
    const kept = new Set(picked[a].slice(0, MENU_TAG_LIMITS[a]))
    if (kept.size === 0) continue
    out[a] = MENU_TAGS[a].filter((x) => kept.has(x.id)).map((x) => x.id)
  }
  return out
}

/**
 * 메뉴 축 세부를 고르면 미리 켜는 꼬리표. 같은 뜻이 확실한 것만 (사용자가 끌 수 있다, 서버는 스스로 더하지 않는다).
 * 코스 Main 의 조리법 칸(main.grill ~ main.smoke)은 메뉴 메인 요리 세부를 내려 쓴 것이라 id·뜻이 같아 이 표를 같이 쓴다
 * (대표 원문 "현재 카테고리를 여기로 내림", course-taxonomy.ts ROLE_STYLES.main)
 */
export const MENU_IMPLIED_TAGS: Record<string, MenuTags> = {
  'main.smoke': { technique: ['smoking'] },
}

/**
 * id 끝 조각이 기법 id 와 같지만 기법 칸이 아닌 코스 스타일 칸. Main 의 컨핏·숙성은 1.0.9 의 컨핏 기법 칸 id 를 이어받은
 * 스타일 칸이다 (컨핏과 숙성을 함께 뜻해 기법 하나를 미리 켜지 않는다, course-taxonomy.ts ROLE_STYLES.main)
 */
const COURSE_STYLE_CELLS_WITH_TECHNIQUE_ID = new Set(['main.confit'])

/**
 * 코스·테이스팅 세부(`역할.항목`)가 기법 칸이면 그 기법 id (main.sous-vide → sous-vide, dessert.modernist → modernist).
 * 기법 칸은 id 끝 조각이 기법 꼬리표 id 와 같다 (course-taxonomy.ts). 아니면 null
 */
export function courseTechniqueOf(subId: string | null | undefined): string | null {
  const dot = subId?.indexOf('.') ?? -1
  if (!subId || dot < 0 || COURSE_STYLE_CELLS_WITH_TECHNIQUE_ID.has(subId)) return null
  const leaf = subId.slice(dot + 1)
  return findMenuTag('technique', leaf) ? leaf : null
}

/**
 * 세부를 고르면 미리 켜는 꼬리표 (꼬리표 화면에서 끌 수 있다). 메뉴 축은 MENU_IMPLIED_TAGS 표.
 * 코스·테이스팅 축은 기법 칸을 고르면 그 기법 (2026-11-01 점검 10번: 앞에서 고른 기법이 꼬리표 화면에 켜져 있게),
 * 그 밖의 칸은 메뉴와 같은 표 (Main 의 훈연·바비큐 → 스모킹·훈연). 식품 축은 꼬리표가 없어 늘 {}
 */
export function impliedMenuTags(
  axis: string | null | undefined,
  subId: string | null | undefined,
): MenuTags {
  const technique = axis === 'course' ? courseTechniqueOf(subId) : null
  if (technique) return { technique: [technique] }
  const implied =
    (axis === 'menu' || axis === 'course') && subId ? MENU_IMPLIED_TAGS[subId] : undefined
  if (!implied) return {}
  const out: MenuTags = {}
  for (const a of MENU_TAG_AXES) {
    const ids = implied[a]
    if (ids?.length) out[a] = [...ids]
  }
  return out
}

/**
 * 커뮤니티 거르기에서 같은 것으로 보는 값들 (2026-11-01). 넓은 옛 값(가금)으로 거르면 그 묶음의 세부 값(닭·오리 등)이 붙은
 * 글도, 세부 값(닭)으로 거르면 넓은 옛 값(가금)만 붙은 옛 글도 걸린다. 묶음이 없는 값과 해산물은 자기 하나
 */
export function menuTagFamily(axis: MenuTagAxis, id: string): string[] {
  const tag = findMenuTag(axis, id)
  if (!tag?.group) return [id]
  const group = MENU_TAGS[axis].filter((x) => x.group === tag.group)
  if (tag.hiddenInPicker) return group.map((x) => x.id)
  return [id, ...group.filter((x) => x.hiddenInPicker).map((x) => x.id)]
}

export function countMenuTags(tags: MenuTags | null | undefined): number {
  if (!tags) return 0
  return MENU_TAG_AXES.reduce((n, a) => {
    const ids = tags[a]
    return n + (Array.isArray(ids) ? ids.length : 0)
  }, 0)
}

/**
 * 식이 제한 꼬리표의 표시 단정 금지 (반박 검토 H2). 재료만 빼서는 글루텐프리·견과류 무함유 같은 표시를 할 수 없다
 * (함량 기준·같은 시설 혼입 주의 문구). 라벨 초안(complianceSummary)·baseNote 에 인증처럼 쓰지 않게 한다
 */
export const DIET_CLAIM_LINE =
  '비건, 베지테리안, 글루텐프리, 유제품프리, 견과류프리, 알러지 주요 8종 고려, 할랄은 재료를 고르는 기준이다. complianceSummary·baseNote 에 표시·인증 문구로 단정하지 않는다. caution 에 표시 전 검사·인증 확인을, steps 의 qc 에 교차오염 관리를 적는다.'

/**
 * 식이 제한 줄 ("- 비건: hint"). 생성 프롬프트(menuTagsPromptLines)와 AI 심사(gate.ts judge)가 같은 문장을 쓴다.
 * 모르는 값은 건너뛴다. 정리(normalizeMenuTags)한 꼬리표를 받는다고 본다
 */
export function menuDietLines(tags: MenuTags | null | undefined): string[] {
  return (tags?.diet ?? []).flatMap((id) => {
    const tag = findMenuTag('diet', id)
    return tag ? [`- ${tag.ko}: ${tag.hint}`] : []
  })
}

/**
 * 프롬프트용 줄 목록 (한국어). 빈 꼬리표면 [].
 * 두 덩어리다 (2026-11-01, 명세 5장 "식이 제한은 실제로 지켜야 한다"):
 *  1. 힌트 — 머리 한 줄, 값마다 "- 축: 값. hint" (식이 제한 밖의 축), 코스 역할이 있으면 인분·가격 기준 한 줄
 *  2. 식이 제한 — 지켜야 하는 조건이라는 머리, 값마다 "- 값: hint", 적용 범위(대체재까지)와 분류 기본 재료를 바꾸는 법,
 *     표시·인증 문구로 단정하지 말라는 마지막 줄(DIET_CLAIM_LINE)
 * 분석 모드(mode 'analyze')는 사용자 레시피의 재료를 바꾸지 않는 모드라(ANALYZE_RULES) 바꾸라는 줄 대신
 * 어긴 재료를 caution 에 적고 대체재로 고칠 길을 보이라는 줄을 쓴다.
 * 모르는 값은 건너뛴다 (정리 전 값을 받아도 프롬프트에 id 가 새지 않게)
 */
export function menuTagsPromptLines(tags: MenuTags | null | undefined, mode?: Mode): string[] {
  if (!tags) return []
  const hints: string[] = []
  for (const a of MENU_TAG_AXES) {
    if (a === 'diet') continue
    for (const id of tags[a] ?? []) {
      const tag = findMenuTag(a, id)
      if (tag) hints.push(`- ${menuTagAxisLabel(a, 'ko')}: ${tag.ko}. ${tag.hint}`)
    }
  }
  const diet = menuDietLines(tags)
  const hasCourse = (tags.course ?? []).some((id) => !!findMenuTag('course', id))
  return [
    ...(hints.length > 0
      ? [
          '[꼬리표: 사용자가 고른 선택 정보. 대·중분류 골격은 그대로 두고 재료·공정·원가·가격에 반영한다]',
          ...hints,
          hasCourse
            ? '코스 역할이 있으면 1인분 중량은 코스 역할 설명을 따른다(대·중분류의 인분 기본값보다 우선). 판매가는 코스 전체 가격이 아니라 코스 가격 중 이 접시 몫으로 낸다.'
            : '',
        ]
      : []),
    ...(diet.length > 0
      ? [
          mode === 'analyze'
            ? '[식이 제한: 사용자가 고른 조건. 분석 모드라 레시피는 그대로 두고, 조건과 어긋나는 곳을 알린다]'
            : '[식이 제한: 힌트가 아니라 반드시 지키는 조건. 대·중분류의 기본 재료, 사용자 설명, 방향 시드보다 우선한다]',
          ...diet,
          mode === 'analyze'
            ? '분석 모드라 사용자 레시피의 재료는 바꾸지 않는다. 식이 제한을 어기는 재료가 있으면 caution 에 그 재료를 적고, 그 재료의 substitutes 에는 제한을 지키는 대체재만 넣는다. allergens 는 사용자 레시피 그대로의 배합으로 적는다.'
            : '식이 제한은 재료·양념·육수·소스·튀김옷·마감 재료와 대체재(substitutes)까지 모두에 적용한다. 어기는 재료는 하나도 쓰지 않는다. 분류가 기본으로 쓰는 재료가 제한에 걸리면 같은 역할의 허용 재료로 바꾸고, 바꾼 것을 internal.assumptions 에 적는다. allergens 는 바꾼 뒤의 실제 배합으로 적는다.',
          DIET_CLAIM_LINE,
        ]
      : []),
  ].filter(Boolean)
}
